import React, { useState, useEffect } from "react";
import { Building2, ChevronDown, Plus, Check, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// Iter-146 · Shared source of truth — keeps this dialog aligned with Signup
// and Settings so all 3 flows offer the SAME 6 canonical industries.
import { ENTITY_TYPES, INDUSTRY_TYPES } from "@/lib/industry-types";

/**
 * OrgSwitcher · Iter-119
 * - Persistent, prominent "+ Add Company" button (accent-outlined) directly
 *   below the current-org pill so multi-company creation is a one-click
 *   affordance without opening the dropdown first.
 * - Create dialog upgraded from name-only to entity type + industry + dealer
 *   toggle so the new org gets the CORRECT default Chart of Accounts.
 */

export default function OrgSwitcher({ org }) {
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", entity_type: "private_limited",
    industry_type: "trading_inventory", is_dealership: false,
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setOrgs((await api.get("/organizations/mine")).data || []); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const switchTo = async (id) => {
    if (id === org?.id) return;
    setBusy(true);
    try {
      const r = await api.post(`/auth/switch-org/${id}`);
      // Wipe every cache-y bit BEFORE reload so lists don't briefly show
      // the previous tenant's rows during hydrate.
      try {
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith("finops_") || k.startsWith("z360_")) {
            localStorage.removeItem(k);
          }
        });
      } catch { /* noop */ }
      toast.success(`Switched to ${r.data.org.name}`);
      // Full navigation (not reload) — nukes React state, remounts everything
      // rooted from /auth/me which will now return the new org's payload.
      window.location.href = "/app/dashboard";
    } catch (e) { toast.error(e?.response?.data?.detail || "Switch failed"); }
    setBusy(false);
  };

  const openCreate = () => {
    setForm({ name: "", entity_type: "private_limited",
                industry_type: "trading_inventory", is_dealership: false });
    setOpen(false);
    setCreateOpen(true);
  };

  const createOrg = async () => {
    const name = (form.name || "").trim();
    if (!name) return toast.error("Company name is required");
    setBusy(true);
    try {
      const r = await api.post("/organizations", {
        name, entity_type: form.entity_type,
        industry_type: form.industry_type,
        is_dealership: !!form.is_dealership,
      });
      const acct = r.data.coa_accounts_seeded;
      toast.success(`Created ${r.data.org.name} · ${acct} Chart of Accounts seeded`);
      setCreateOpen(false);
      window.location.reload();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(Array.isArray(raw) ? raw[0]?.msg : (raw || "Create failed"));
    }
    setBusy(false);
  };

  return (
    <div className="mt-3 relative" data-testid="org-switcher">
      {/* Current org pill */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-xs text-white/70 hover:text-white p-2 border border-white/10 hover:border-white/30 transition-colors"
        data-testid="sidebar-org-name" title={org?.name}
      >
        <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="truncate flex-1 text-left">{org?.name || "Select org"}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* PERSISTENT +Add Company button — always visible under the org pill */}
      <button onClick={openCreate}
        className="mt-1.5 w-full flex items-center justify-center gap-1.5 text-[11px] text-accent/90 hover:text-accent bg-accent/5 hover:bg-accent/15 border border-dashed border-accent/40 hover:border-accent transition-colors py-1.5 rounded-sm"
        data-testid="sidebar-add-company">
        <Plus className="w-3 h-3" /> Add Company
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-neutral-900 border border-white/10 z-50 shadow-xl" data-testid="org-dropdown">
          {orgs.length === 0 ? (
            <div className="p-3 text-xs text-white/50">Loading…</div>
          ) : (
            orgs.map((o) => (
              <button
                key={o.id} onClick={() => switchTo(o.id)} disabled={busy}
                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-white/5 ${o.is_active ? "bg-white/10" : ""}`}
                data-testid={`org-option-${o.id.slice(0, 8)}`}>
                <div className="flex-1 truncate">
                  <div className="text-white/90 truncate">{o.name}</div>
                  <div className="text-[10px] text-white/40 font-mono">{o.gstin || o.entity_type}</div>
                </div>
                {o.is_active && <Check className="w-3.5 h-3.5 text-green-500" />}
              </button>
            ))
          )}
          <button onClick={openCreate}
            className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-white/5 border-t border-white/10 text-accent"
            data-testid="org-create-btn">
            <Plus className="w-3.5 h-3.5" /> Create new organization
          </button>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-org-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent" /> Add a new company
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] font-medium">Company name</Label>
              <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Acme Motors Pvt Ltd" className="mt-1 h-10"
                data-testid="create-org-name" autoFocus />
            </div>

            <div>
              <Label className="text-[11px] font-medium">Entity type</Label>
              <select value={form.entity_type}
                onChange={(e) => setForm((s) => ({ ...s, entity_type: e.target.value }))}
                className="mt-1 w-full h-10 rounded-sm border border-border bg-background px-2 text-sm"
                data-testid="create-org-entity">
                {ENTITY_TYPES.map((et) => (
                  <option key={et.v} value={et.v}>{et.l}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-[11px] font-medium">Industry</Label>
              {/* Iter-146 · Card grid mirrors the Signup UX so both flows feel identical */}
              <div className="mt-1 grid grid-cols-2 gap-1.5" data-testid="create-org-industry-grid">
                {INDUSTRY_TYPES.map((it) => {
                  const on = form.industry_type === it.v;
                  return (
                    <button key={it.v} type="button"
                      onClick={() => setForm((s) => ({ ...s, industry_type: it.v }))}
                      className={`text-left rounded-sm border p-2 min-h-[58px] transition-colors ${on ? "border-accent bg-accent/10" : "border-border hover:border-accent/40"}`}
                      data-testid={`create-org-industry-${it.v}`}>
                      <div className="text-[12px] font-medium leading-tight">{it.l}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{it.sub}</div>
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Determines the default Chart of Accounts (124-164 accounts) & sidebar modules.
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm select-none cursor-pointer"
              data-testid="create-org-dealer-label">
              <input type="checkbox" checked={form.is_dealership}
                onChange={(e) => setForm((s) => ({ ...s, is_dealership: e.target.checked }))}
                className="rounded" data-testid="create-org-dealer" />
              <span>Auto / Motor dealership <span className="text-muted-foreground">(adds specialised accounts — CD/CIF, WSF, spares, workshop)</span></span>
            </label>

            <div className="text-[11px] text-muted-foreground bg-muted/40 border border-border rounded-sm p-2 leading-relaxed">
              <b>What happens next:</b> a fresh company is created and Chart of Accounts + branches + default doc-type numbering are seeded. You'll auto-switch into the new org on save.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="create-org-cancel">Cancel</Button>
            <Button onClick={createOrg} disabled={busy || !(form.name || "").trim()}
              className="bg-accent hover:bg-accent/90 text-white"
              data-testid="create-org-confirm">
              {busy ? "Creating…" : "Create company"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
